<app-header></app-header>

<div class="edit-listing-container">
  <div *ngIf="loading" class="loading-container">
    <div class="loading-spinner" aria-label="Loading"></div>
    <p>Loading listing details...</p>
  </div>

  <div *ngIf="error && !loading" class="error-container">
    <div class="error-card">
      <h3>Unable to Load Listing</h3>
      <p>{{ error }}</p>
      <button class="btn-retry" (click)="goBack()" type="button">
        Go Back
      </button>
    </div>
  </div>

  <div *ngIf="!loading && !error && listing" class="edit-form-container">
    <header class="edit-header">
      <button
        class="btn-back"
        (click)="goBack()"
        type="button"
        aria-label="Go back to listing"
      >
        <span class="back-icon">←</span>
        Back to Listing
      </button>

      <div class="listing-info">
        <h1>Edit Listing</h1>
        <div class="listing-meta">
          <span class="category-badge">{{ getCategoryName() }}</span>
          <span class="subcategory-badge">{{ getSubcategoryName() }}</span>
        </div>
        <p class="listing-title">{{ listing.title }}</p>
      </div>
    </header>

    <form
      [formGroup]="listingForm"
      (ngSubmit)="onSubmit()"
      class="edit-form"
      novalidate
    >
      <section class="form-section image-section">
        <h2 class="section-title">
          <span class="section-icon" aria-hidden="true">📷</span>
          Images ({{ allImages.length }} / {{ maxImages }}) -
          {{ listing.plan_name || "Free" }} Plan
          <span *ngIf="dropZoneActive" class="drag-instruction">
            Drag images to reorder them
          </span>
        </h2>

        <div class="image-management">
          <div *ngIf="allImages.length > 0" class="image-plan-info">
            <div class="image-counter">
              <span
                class="counter-text"
                [class.approaching-limit]="allImages.length > maxImages * 0.8"
                [class.at-limit]="allImages.length >= maxImages"
              >
                {{ allImages.length }}/{{ maxImages }} images uploaded ({{
                  listing.plan_name || "Free"
                }}
                plan limit)
              </span>
            </div>

            <div
              class="plan-validation-error"
              *ngIf="getImagePlanValidationError()"
            >
              <div class="error-icon" aria-hidden="true">⚠️</div>
              {{ getImagePlanValidationError() }}
            </div>
          </div>

          <div *ngIf="allImages.length > 0" class="all-images">
            <h3>Your Images <small>(drag to reorder)</small></h3>
            <div
              class="image-grid drag-enabled"
              [class.drop-zone-active]="dropZoneActive"
              role="grid"
              aria-label="Image gallery"
            >
              <div
                *ngFor="
                  let imageItem of allImages;
                  let i = index;
                  trackBy: trackByImageId
                "
                class="image-item draggable-image"
                [class.dragging]="draggedIndex === i"
                [class.is-new]="imageItem.isNew"
                draggable="true"
                (dragstart)="onDragStart($event, imageItem, i)"
                (dragend)="onDragEnd($event)"
                (dragover)="onDragOver($event, i)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event, i)"
                role="gridcell"
                [attr.aria-label]="
                  'Image ' + (i + 1) + (imageItem.isNew ? ' (new)' : '')
                "
              >
                <img
                  [src]="imageItem.url"
                  [alt]="'Image ' + (i + 1)"
                  class="image-preview"
                  loading="lazy"
                />
                <div class="image-overlay" aria-hidden="true">
                  <div class="image-number">{{ i + 1 }}</div>
                  <button
                    type="button"
                    class="btn-remove-image"
                    (click)="removeImage(imageItem)"
                    [attr.aria-label]="'Remove image ' + (i + 1)"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                  <div class="drag-handle" title="Drag to reorder">
                    <span aria-hidden="true">⋮⋮</span>
                  </div>
                </div>
                <div
                  *ngIf="imageItem.isNew"
                  class="new-badge"
                  aria-label="New image"
                >
                  NEW
                </div>
              </div>
            </div>
          </div>

          <div
            class="image-upload"
            [class.disabled]="allImages.length >= maxImages"
          >
            <input
              type="file"
              id="imageUpload"
              multiple
              accept="image/*"
              (change)="onFileSelected($event)"
              class="file-input"
              [disabled]="allImages.length >= maxImages"
              aria-describedby="upload-hint"
            />
            <label
              for="imageUpload"
              class="upload-label"
              [class.disabled]="allImages.length >= maxImages"
            >
              <span class="upload-icon" aria-hidden="true">+</span>
              <span *ngIf="allImages.length < maxImages">Add Images</span>
              <span *ngIf="allImages.length >= maxImages"
              >{{ listing.plan_name || "Free" }} plan limit reached ({{
                  maxImages
                }}
                images)</span
              >
            </label>
            <p
              class="upload-hint"
              id="upload-hint"
              *ngIf="allImages.length < maxImages"
            >
              Select up to {{ maxImages - allImages.length }} more images (max
              20MB each) - {{ listing.plan_name || "Free" }} plan limit
            </p>
          </div>
        </div>
      </section>

      <section
        *ngFor="
          let section of fieldSections | keyvalue;
          trackBy: trackBySection
        "
        class="form-section"
      >
        <h2 class="section-title">
          <span class="section-icon" aria-hidden="true">📝</span>
          {{ section.key }}
        </h2>

        <div class="field-grid">
          <div
            *ngFor="let field of section.value; trackBy: trackByField"
            class="field-container"
            [class.field-full-width]="field.type === 'textarea'"
          >
            <div
              *ngIf="
                field.type === 'text' ||
                field.type === 'email' ||
                field.type === 'tel'
              "
              class="form-field"
            >
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <input
                [type]="field.type"
                [id]="field.name"
                [formControlName]="field.name"
                [placeholder]="field.placeholder || ''"
                class="form-input"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              />
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'number'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <input
                type="number"
                [id]="field.name"
                [formControlName]="field.name"
                [placeholder]="field.placeholder || ''"
                [min]="field.min ?? null"
                [max]="field.max ?? null"
                [step]="field.step || 'any'"
                class="form-input"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              />
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'date'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <input
                type="date"
                [id]="field.name"
                [formControlName]="field.name"
                class="form-input"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              />
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'textarea'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                >*</span
                >
              </label>
              <textarea
                [id]="field.name"
                [formControlName]="field.name"
                [placeholder]="field.placeholder || ''"
                [rows]="field.rows || 4"
                class="form-textarea"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
                (input)="onDescriptionChange()"
              ></textarea>

              <div
                *ngIf="field.name === 'description'"
                class="character-counter"
              >
                <span
                  class="counter-text"
                  [class.approaching-limit]="
                    getDescriptionLength() > maxDescriptionLength * 0.8
                  "
                  [class.at-limit]="
                    getDescriptionLength() >= maxDescriptionLength
                  "
                >
                  {{ getDescriptionLength() }}/{{
                    maxDescriptionLength
                  }}
                  characters ({{ listing.plan_name || "Free" }} plan limit)
                </span>

                <div class="plan-breakdown" *ngIf="getDescriptionLength() > 0">
                  <div
                    class="breakdown-item"
                    [class.exceeds]="getDescriptionLength() > 300"
                    [class.fits]="getDescriptionLength() <= 300"
                  >
                    <span class="plan-name">Free (300)</span>
                    <span class="status">{{
                        getDescriptionLength() <= 300 ? "✓" : "⚠"
                      }}</span>
                  </div>
                  <div
                    class="breakdown-item"
                    [class.exceeds]="getDescriptionLength() > 400"
                    [class.fits]="getDescriptionLength() <= 400"
                  >
                    <span class="plan-name">Starter (400)</span>
                    <span class="status">{{
                        getDescriptionLength() <= 400 ? "✓" : "⚠"
                      }}</span>
                  </div>
                  <div
                    class="breakdown-item"
                    [class.exceeds]="getDescriptionLength() > 500"
                    [class.fits]="getDescriptionLength() <= 500"
                  >
                    <span class="plan-name">Pro (500)</span>
                    <span class="status">{{
                        getDescriptionLength() <= 500 ? "✓" : "⚠"
                      }}</span>
                  </div>
                  <div class="breakdown-item fits">
                    <span class="plan-name">Elite (5000)</span>
                    <span class="status">✓</span>
                  </div>
                </div>

                <div
                  class="plan-validation-error"
                  *ngIf="getDescriptionPlanValidationError()"
                >
                  <div class="error-icon">⚠️</div>
                  {{ getDescriptionPlanValidationError() }}
                </div>
              </div>

              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'select'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                >*</span
                >
              </label>
              <select
                [id]="field.name"
                [formControlName]="field.name"
                class="form-select"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              >
                <option value="">Select {{ field.label }}</option>
                <option
                  *ngFor="let option of field.options; trackBy: trackByOption"
                  [value]="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div
              *ngIf="field.type === 'checkbox'"
              class="form-field checkbox-field"
            >
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  [id]="field.name"
                  [formControlName]="field.name"
                  class="form-checkbox"
                  [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                />
                <span class="checkbox-text">{{ field.label }}</span>
              </label>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'vehicle-selector'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <app-vehicle-selector
                [formControlName]="field.name"
                [required]="field.required || false"
                [class.is-invalid]="isFieldInvalid(field.name)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              ></app-vehicle-selector>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'motorcycle-brand-selector'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <app-motorcycle-brand-selector
                *ngIf="field.type === 'motorcycle-brand-selector'"
                [formControlName]="field.name"
                [class.invalid]="
                listingForm.get(field.name)?.invalid &&
                listingForm.get(field.name)?.touched
              "
              ></app-motorcycle-brand-selector>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div *ngIf="field.type === 'location'" class="form-field">
              <label [for]="field.name" class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <app-location-autocomplete
                [formControlName]="field.name"
                [placeholder]="
                  field.placeholder || 'Start typing a city name...'
                "
                [inputClass]="isFieldInvalid(field.name) ? 'invalid' : ''"
                (locationSelected)="onLocationSelected($event)"
                [attr.aria-describedby]="getFieldDescribedBy(field.name)"
                [attr.aria-invalid]="isFieldInvalid(field.name)"
              ></app-location-autocomplete>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>

            <div
              *ngIf="
                field.type === 'first_registration_dropdown' ||
                field.type === 'registered_until_dropdown'
              "
              class="form-field"
            >
              <label class="field-label">
                {{ field.label }}
                <span
                  *ngIf="field.required"
                  class="required"
                  aria-label="required"
                  >*</span
                >
              </label>
              <div class="dropdown-group">
                <select
                  [id]="field.name + '_month'"
                  class="form-select dropdown-half"
                  [value]="(dateFieldsData[field.name] && dateFieldsData[field.name].month) || ''"
                  (change)="onDateMonthChange($event, field.name)"
                  [class.is-invalid]="isFieldInvalid(field.name)"
                >
                  <option value="">Month</option>
                  <option
                    *ngFor="let month of monthOptions"
                    [value]="month.value"
                    [disabled]="isMonthDisabled(month.value, field.name)"
                  >
                    {{ month.label }}
                  </option>
                </select>

                <select
                  [id]="field.name + '_year'"
                  class="form-select dropdown-half"
                  [value]="(dateFieldsData[field.name] && dateFieldsData[field.name].year) || ''"
                  (change)="onDateYearChange($event, field.name)"
                  [class.is-invalid]="isFieldInvalid(field.name)"
                >
                  <option value="">Year</option>
                  <option
                    *ngFor="let year of getYearOptionsForField(field.name)"
                    [value]="year"
                  >
                    {{ year }}
                  </option>
                </select>
              </div>
              <div
                *ngIf="field.hint"
                class="field-hint"
                [id]="field.name + '-hint'"
              >
                {{ field.hint }}
              </div>
              <div
                *ngIf="isFieldInvalid(field.name)"
                class="field-error"
                [id]="field.name + '-error'"
                role="alert"
              >
                {{ getFieldError(field) }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div *ngIf="error" class="form-error-alert" role="alert">
        <span class="error-icon" aria-hidden="true">⚠</span>
        <span>{{ error }}</span>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="btn-cancel"
          (click)="goBack()"
          [disabled]="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn-save"
          [disabled]="!canSave"
          [attr.aria-busy]="isSubmitting"
        >
          <span *ngIf="!isSubmitting">Save Changes</span>
          <span *ngIf="isSubmitting" class="loading-text">
            <span class="spinner" aria-hidden="true"></span>
            Saving...
          </span>
        </button>
      </div>
    </form>
  </div>
</div>

<div
  *ngIf="showSuccessModal"
  class="modal-overlay"
  (click)="onModalOverlayClick()"
  [@fadeInOut]
  role="dialog"
  aria-modal="true"
  aria-labelledby="modal-title"
>
  <div class="modal-container" (click)="$event.stopPropagation()" [@slideInUp]>
    <div class="modal-header">
      <div class="success-icon-container">
        <div class="success-icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#10b981"
              stroke-width="2"
              fill="#ecfdf5"
            />
            <path
              d="m9 12 2 2 4-4"
              stroke="#10b981"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
      <button
        class="modal-close-btn"
        (click)="closeSuccessModal()"
        aria-label="Close modal"
        type="button"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="m18 6-12 12M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div class="modal-body">
      <h2 class="modal-title" id="modal-title">
        Listing Updated Successfully!
      </h2>
      <div class="modal-content">
        <div class="status-update-info">
          <div class="status-badge pending">
            <span class="status-dot" aria-hidden="true"></span>
            <span>Pending Review</span>
          </div>
          <p class="status-description">
            Your listing has been submitted for review and is now pending
            approval.
          </p>
        </div>

        <div class="next-steps">
          <h3>What happens next?</h3>
          <ul class="steps-list">
            <li class="step-item">
              <div class="step-icon" aria-hidden="true">📋</div>
              <span>An admin will review your changes</span>
            </li>
            <li class="step-item">
              <div class="step-icon" aria-hidden="true">🚀</div>
              <span>Once approved, your listing will be live again</span>
            </li>
          </ul>
        </div>

        <div class="estimated-time">
          <div class="time-info">
            <div class="clock-icon" aria-hidden="true">⏱️</div>
            <div>
              <strong>Estimated Review Time</strong>
              <p>Usually within 24-48 hours</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn-secondary" (click)="goToMyListings()" type="button">
        View My Listings
      </button>
      <button class="btn-primary" (click)="viewUpdatedListing()" type="button">
        View Listing
      </button>
    </div>
  </div>
</div>

<app-footer></app-footer>
